import { NavLink, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect, useCallback } from "react";
import {
  LayoutDashboard, TrendingUp, Link2, BellRing,
  Users, ShieldCheck, LogOut, Activity, CreditCard,
  TicketCheck, MessageCircle, DollarSign,
  Eye, Lightbulb, UserCircle, PanelLeftClose, PanelLeftOpen, Brain, FileText, Contact, UserCog, Building2, BarChart3, BookOpen, ShieldAlert, Scale
} from "lucide-react";
import { clearAuth } from "../store/authSlice";
import { toast } from "sonner";
import api from "../utils/api";

const ROLE_LABELS = {
  super_admin: "Super Admin", local_admin: "Local Admin", owner: "Business Owner",
  consultant: "CFO / Consultant", ops_manager: "Ops Manager", accountant: "Accountant",
  consulting_partner: "Partner"
};

const NAV_SECTIONS = [
  {
    title: null,
    items: [
      { to: "/dashboard", icon: LayoutDashboard, label: "Executive Summary" },
      { to: "/financials", icon: DollarSign, label: "Financial Operations" },
      { to: "/analytics", icon: Eye, label: "Operational Overview" },
      { to: "/crm", icon: Contact, label: "CRM" },
      { to: "/hr", icon: UserCog, label: "HR" },
      { to: "/banking", icon: Building2, label: "Banking" },
      { to: "/data-analytics", icon: BarChart3, label: "Data Analytics" },
      { to: "/benchmarking", icon: Scale, label: "Benchmarking" },
    ],
  },
  {
    title: "Insights",
    items: [
      { to: "/ai-advisor", icon: Brain, label: "AI CFO Advisor" },
      { to: "/forecasting", icon: Lightbulb, label: "Financial Insights" },
      { to: "/predictive", icon: TrendingUp, label: "Predictive Analysis" },
    ],
  },
  {
    title: "Operations",
    items: [
      { to: "/reports", icon: FileText, label: "Reports" },
      { to: "/integrations", icon: Link2, label: "Integration" },
      { to: "/glossary", icon: BookOpen, label: "Glossary" },
      { to: "/subscriptions", icon: CreditCard, label: "Subscription" },
      { to: "/chat", icon: MessageCircle, label: "Team Chat" },
    ],
  },
  {
    title: "Support",
    items: [
      { to: "/help", icon: TicketCheck, label: "Support Tickets", hasBadge: true },
      { to: "/alerts", icon: BellRing, label: "Notifications" },
      { to: "/compliance", icon: ShieldAlert, label: "Compliance" },
    ],
  },
  {
    title: "Account",
    items: [
      { to: "/settings", icon: UserCircle, label: "Profile" },
      { to: "/team", icon: Users, label: "Team", roles: ["owner", "local_admin", "super_admin"] },
    ],
  },
];

const ADMIN_LINK = { to: "/admin/dashboard", icon: ShieldCheck, label: "Admin Panel", roles: ["super_admin", "local_admin"] };
const PARTNER_LINK = { to: "/partner/dashboard", icon: Activity, label: "Partner Portal", roles: ["consulting_partner"] };

function NavItem({ to, icon: Icon, label, badge, collapsed }) {
  return (
    <NavLink to={to} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="group relative flex items-center rounded-lg font-medium transition-all"
      style={({ isActive: active }) => ({
        backgroundColor: active ? "#1B75BA" : "transparent",
        color: active ? "#FFFFFF" : "#B8D8F2",
        padding: collapsed ? "10px" : "9px 12px",
        justifyContent: collapsed ? "center" : "flex-start",
        gap: collapsed ? 0 : 10,
        fontSize: 14,
      })}>
      <Icon className="flex-shrink-0" style={{ width: 18, height: 18 }} />
      {!collapsed && <span className="flex-1 truncate">{label}</span>}
      {!collapsed && badge > 0 && (
        <span data-testid="open-tickets-badge"
          className="min-w-[20px] h-[20px] flex items-center justify-center rounded-full text-[10px] font-bold text-white px-1"
          style={{ backgroundColor: "#EA580C" }}>
          {badge > 99 ? "99+" : badge}
        </span>
      )}
      {collapsed && badge > 0 && (
        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#EA580C" }} />
      )}
      {/* Tooltip on hover when collapsed */}
      {collapsed && (
        <div className="absolute left-full ml-3 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-[60]"
          style={{ backgroundColor: "#1C2040", color: "#FFFFFF", boxShadow: "0 4px 12px rgba(0,0,0,0.2)" }}>
          {label}
          <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-2 h-2 rotate-45" style={{ backgroundColor: "#1C2040" }} />
        </div>
      )}
    </NavLink>
  );
}

export default function Sidebar({ collapsed, setCollapsed, viewport = "desktop", mobileOpen = false, setMobileOpen = () => {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, token } = useSelector((s) => s.auth);
  const [openTickets, setOpenTickets] = useState(0);
  const isMobile = viewport === "mobile";
  const isTablet = viewport === "tablet";

  const fetchTicketCount = useCallback(async () => {
    try {
      const res = await api.get("/tickets?status=open");
      setOpenTickets(res.data.open || 0);
    } catch {}
  }, []);

  useEffect(() => {
    if (token) {
      fetchTicketCount();
      const interval = setInterval(fetchTicketCount, 60000);
      return () => clearInterval(interval);
    }
  }, [token, fetchTicketCount]);

  const handleLogout = () => {
    localStorage.removeItem("onpoint_token");
    localStorage.removeItem("onpoint_refresh");
    dispatch(clearAuth());
    toast.success("Signed out successfully");
    navigate("/login");
  };

  const initials = user?.name ? user.name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase() : "?";

  // Mobile: sidebar is a slide-in drawer with overlay
  if (isMobile) {
    return (
      <>
        {/* Backdrop */}
        {mobileOpen && (
          <div
            data-testid="sidebar-backdrop"
            onClick={() => setMobileOpen(false)}
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          />
        )}
        <aside
          className="fixed left-0 top-0 h-screen flex flex-col z-50 overflow-hidden transition-transform duration-300"
          style={{
            width: 260,
            backgroundColor: "#282560",
            transform: mobileOpen ? "translateX(0)" : "translateX(-100%)",
            boxShadow: mobileOpen ? "0 12px 32px rgba(0,0,0,0.25)" : "none",
          }}
          data-testid="sidebar-mobile"
        >
          <div className="flex items-center justify-between flex-shrink-0" style={{ backgroundColor: "#FFFFFF", borderBottom: "1px solid #EEF0F4", padding: "12px 16px" }}>
            <img src="/images/logo-colored.png" alt="OnPoint BI" className="object-contain" style={{ height: 36, width: "auto" }} />
            <button data-testid="sidebar-close-mobile" onClick={() => setMobileOpen(false)} className="p-1.5 rounded-lg hover:bg-gray-100" style={{ color: "#1C2040" }}>
              <PanelLeftClose style={{ width: 18, height: 18 }} />
            </button>
          </div>

          <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto">
            {NAV_SECTIONS.map((section, si) => (
              <div key={si} onClick={() => setMobileOpen(false)}>
                {section.title && (
                  <p className="px-3 pt-4 pb-1.5 text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: "rgba(255,255,255,0.3)" }}>
                    {section.title}
                  </p>
                )}
                {section.items.map(({ to, icon, label, roles, hasBadge }) => {
                  if (roles && !roles.includes(user?.role)) return null;
                  return <NavItem key={to} to={to} icon={icon} label={label} badge={hasBadge ? openTickets : 0} collapsed={false} />;
                })}
              </div>
            ))}
            {ADMIN_LINK.roles.includes(user?.role) && (
              <div onClick={() => setMobileOpen(false)} style={{ borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: 8, paddingTop: 8 }}>
                <NavItem to={ADMIN_LINK.to} icon={ADMIN_LINK.icon} label={ADMIN_LINK.label} collapsed={false} />
              </div>
            )}
            {PARTNER_LINK.roles.includes(user?.role) && (
              <div onClick={() => setMobileOpen(false)} style={{ borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: 8, paddingTop: 8 }}>
                <NavItem to={PARTNER_LINK.to} icon={PARTNER_LINK.icon} label={PARTNER_LINK.label} collapsed={false} />
              </div>
            )}
          </nav>

          <div className="p-2 flex-shrink-0" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
            <div className="flex items-center gap-3 p-2.5 rounded-xl" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: "#1B75BA" }}>
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-[14px] font-medium truncate leading-tight">{user?.name}</p>
                <span className="text-[11px]" style={{ color: "#7DBDE9" }}>{ROLE_LABELS[user?.role] || user?.role}</span>
              </div>
              <button data-testid="logout-btn-mobile" onClick={handleLogout} className="p-1.5 rounded-lg hover:bg-white/10" style={{ color: "rgba(255,255,255,0.6)" }} title="Sign out">
                <LogOut style={{ width: 15, height: 15 }} />
              </button>
            </div>
          </div>
        </aside>
      </>
    );
  }

  return (
    <aside className="fixed left-0 top-0 h-screen flex flex-col z-50 overflow-hidden transition-all duration-300"
      style={{ width: collapsed ? 72 : 240, backgroundColor: "#282560" }}
      data-testid="sidebar">

      {/* Logo */}
      <div className="flex items-center justify-center flex-shrink-0 transition-all duration-300"
        style={{ backgroundColor: "#FFFFFF", borderBottom: "1px solid #EEF0F4", padding: collapsed ? "14px 6px" : "12px 16px", minHeight: collapsed ? 56 : "auto" }}>
        <img src="/images/logo-colored.png" alt="OnPoint BI" className="object-contain" 
          style={{ width: collapsed ? 56 : "80%", height: "auto" }} data-testid="sidebar-logo" />
      </div>

      {/* Toggle - hidden on tablet (forced collapsed) */}
      {!isTablet && (
        <div className="flex justify-end px-2 pt-2 pb-0.5 flex-shrink-0">
          <button data-testid="sidebar-toggle" onClick={() => setCollapsed(!collapsed)}
            className="p-1.5 rounded-lg transition-colors hover:bg-white/10"
            style={{ color: "rgba(255,255,255,0.5)" }}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
            {collapsed ? <PanelLeftOpen style={{ width: 16, height: 16 }} /> : <PanelLeftClose style={{ width: 16, height: 16 }} />}
          </button>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 px-2 pb-2 space-y-0.5 overflow-hidden">
        {NAV_SECTIONS.map((section, si) => (
          <div key={si}>
            {section.title && !collapsed && (
              <p className="px-3 pt-5 pb-1.5 text-[10px] font-bold uppercase tracking-[0.12em]"
                style={{ color: "rgba(255,255,255,0.3)" }}>
                {section.title}
              </p>
            )}
            {section.title && collapsed && <div className="my-2 mx-2 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }} />}
            {section.items.map(({ to, icon, label, roles, hasBadge }) => {
              if (roles && !roles.includes(user?.role)) return null;
              return <NavItem key={to} to={to} icon={icon} label={label} badge={hasBadge ? openTickets : 0} collapsed={collapsed} />;
            })}
          </div>
        ))}

        {ADMIN_LINK.roles.includes(user?.role) && (
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: 8, paddingTop: 8 }}>
            <NavItem to={ADMIN_LINK.to} icon={ADMIN_LINK.icon} label={ADMIN_LINK.label} collapsed={collapsed} />
          </div>
        )}
        {PARTNER_LINK.roles.includes(user?.role) && (
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: 8, paddingTop: 8 }}>
            <NavItem to={PARTNER_LINK.to} icon={PARTNER_LINK.icon} label={PARTNER_LINK.label} collapsed={collapsed} />
          </div>
        )}
      </nav>

      {/* User section */}
      <div className="p-2 flex-shrink-0" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        {collapsed ? (
          <div className="flex flex-col items-center gap-2 py-1">
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: "#1B75BA" }}>
              {initials}
            </div>
            <button data-testid="logout-btn" onClick={handleLogout}
              className="p-1.5 rounded-lg transition-colors hover:bg-white/10"
              style={{ color: "rgba(255,255,255,0.4)" }} title="Sign out">
              <LogOut style={{ width: 15, height: 15 }} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3 p-2.5 rounded-xl" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: "#1B75BA" }}>
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-[14px] font-medium truncate leading-tight">{user?.name}</p>
              <span className="text-[11px]" style={{ color: "#7DBDE9" }}>{ROLE_LABELS[user?.role] || user?.role}</span>
            </div>
            <button data-testid="logout-btn" onClick={handleLogout}
              className="p-1.5 rounded-lg transition-colors hover:bg-white/10"
              style={{ color: "rgba(255,255,255,0.4)" }} title="Sign out">
              <LogOut style={{ width: 15, height: 15 }} />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
